import { createSelector } from 'reselect';
import {
  getSummary,
  getAnalysis,
  getFormatting,
} from '@/pages/CompanyReport/redux/selectors';
import transformToTimeSeries, {
  seriesArraySortDesending,
} from '@/pages/CompanyReport/utils/tranformToTimeSeries';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';

interface EarningsAndRevenueHistory {
  cashflow: number[][];
  cashflowMax: number;
  cashflowMin: number;
  freeCashFlow: number[][];
  freeCashFlowMax: number;
  freeCashFlowMin: number;
  currencyISO: string;
  currencyUnit: number;
  earnings: number[][];
  earningsMax: number;
  earningsMin: number;
  operatingExpenses: number[][];
  operatingExpensesMax: number;
  operatingExpensesMin: number;
  revenue: number[][];
  revenueMax: number;
  revenueMin: number;
}

export const getEarningsAndRevenueHistorySelector = createSelector(
  [getAnalysis, getFormatting],
  (analysis, formatting): EarningsAndRevenueHistory => {
    const revenue = analysis.legacyChartData.pastRevenueActuals;
    const earnings = analysis.legacyChartData.pastNetIncome;
    const freeCashFlow = analysis.legacyChartData.pastFreeCashFlow;
    const cashflow = analysis.legacyChartData.pastCashFlow;
    const operatingExpenses =
      analysis.legacyChartData.healthOperatingExpensesTotal;
    const revenueMax = maxBy(revenue, (n) => n[1]);
    const earningsMax = maxBy(earnings, (n) => n[1]);
    const freeCashFlowMax = maxBy(freeCashFlow, (n) => n[1]);
    const cashflowMax = maxBy(cashflow, (n) => n[1]);
    const operatingExpensesMax = maxBy(operatingExpenses, (n) => n[1]);

    const revenueMin = minBy(revenue, (n) => n[1]);
    const earningsMin = minBy(earnings, (n) => n[1]);
    const freeCashFlowMin = minBy(freeCashFlow, (n) => n[1]);
    const cashflowMin = minBy(cashflow, (n) => n[1]);
    const operatingExpensesMin = minBy(operatingExpenses, (n) => n[1]);

    return {
      cashflow,
      cashflowMax: cashflowMax ? cashflowMax[1] : 0,
      cashflowMin: cashflowMin ? cashflowMin[1] : 0,
      freeCashFlow,
      freeCashFlowMax: freeCashFlowMax ? freeCashFlowMax[1] : 0,
      freeCashFlowMin: freeCashFlowMin ? freeCashFlowMin[1] : 0,
      currencyISO: formatting.reportingCurrencyISO,
      currencyUnit: formatting.reportingUnit,
      earnings,
      earningsMax: earningsMax ? earningsMax[1] : 0,
      earningsMin: earningsMin ? earningsMin[1] : 0,
      operatingExpenses,
      operatingExpensesMax: operatingExpensesMax ? operatingExpensesMax[1] : 0,
      operatingExpensesMin: operatingExpensesMin ? operatingExpensesMin[1] : 0,
      revenue,
      revenueMax: revenueMax ? revenueMax[1] : 0,
      revenueMin: revenueMin ? revenueMin[1] : 0,
    };
  }
);

export const getEarningsAndRevenueHistoryRawDataProps = createSelector(
  [getSummary, getAnalysis, getFormatting],
  (summary, analysis, formatting) => {
    const { legacyChartData } = analysis;
    const pastRevenue = legacyChartData.pastRevenueActuals;
    const pastNetIncome = legacyChartData.pastNetIncome;
    const pastGAExpenses = legacyChartData.generalAdministrativeExpense;
    const pastRnDExpenses = legacyChartData.researchAndDevExpenses;

    const pastRevenueTimeSeries = transformToTimeSeries([
      pastRevenue,
      pastNetIncome,
      pastGAExpenses,
      pastRnDExpenses,
    ]);

    pastRevenueTimeSeries.sort(seriesArraySortDesending);

    return {
      uniqueSymbol: summary.uniqueSymbol,
      reportingCurrencyISO: formatting.reportingCurrencyISO,
      reportingUnit: formatting.reportingUnitText,
      lastEarningsUpdate: analysis.lastEarningsUpdateTimeStamp,
      companyShortName: summary.shortName,
      pastRevenueData: pastRevenueTimeSeries,
    };
  }
);
